"use client"

import { CompanyAvatar } from "@/components/generic/CompanyAvatar/CompanyAvatar"
import { chatStore$ } from "@/lib/ContactsMap"
import { extractPrettyContactData } from "@/lib/prettify/contact"
import type { TContact } from "@/schemas/contacts"
import { observer, Show, use$ } from "@legendapp/state/react"
import { Avatar, Group, Highlight, Stack } from "@mantine/core"
import { Spotlight } from "@mantine/spotlight"
import classes from "./HeaderSearchSpotlightItem.module.css"
import { useCallback, useMemo } from "react"
import { selectContact } from "@/components/messenger/contacts-section/helpers"
import { headerSearchSpotlight } from "./HeaderSearchSpotlight"
// @deprecated The embedded frontend webphone is disabled. The Chrome extension now provides the webphone sidebar.
// import { useAppStoreContext } from "@/stores/appStore"

interface Props {
  item: TContact
  isMessengerPage: boolean
  searchQuery: string
}

export const HeaderSearchSpotlightItem = observer(({ item, isMessengerPage, searchQuery }: Props) => {
  // @deprecated The embedded frontend webphone is disabled. Opening it from search is a no-op.
  // const { setWebphoneExpanded } = useAppStoreContext((s) => s)

  const prettyContactData = use$(() =>
    extractPrettyContactData({
      mode: "sms",
      fullName: item.ContactFullname,
      email: item.ContactEmail,
      phoneNumber: item.ContactPhoneNumber,
      emails: item.ContactEmails,
      phoneNumbers: item.ContactPhoneNumbers,
    })
  )

  const lastInteraction = use$(() => {
    return chatStore$.chatLastInteraction[item.ContactPersonId].get()
  })

  const contactCompany = use$(() => item.ContactCompanyName ?? "")

  // Get the subline text (phone and/or email)
  const sublineText = useMemo(() => {
    const parts: string[] = []

    // Try multiple sources for phone number (ensure we have a string)
    const phoneRaw = prettyContactData.contactPhoneNumber || item.ContactPhoneNumber || item.ContactPhoneNumbers
    const phone = typeof phoneRaw === "string" ? phoneRaw : null
    if (phone) {
      // If comma-separated, show the first one
      parts.push(phone.includes(",") ? phone.split(",")[0].trim() : phone)
    }

    // Add email if available (ensure we have a string)
    const emailRaw = prettyContactData.contactEmail || item.ContactEmail
    const email = typeof emailRaw === "string" ? emailRaw : null
    if (email) {
      parts.push(email)
    }

    return parts.length > 0 ? parts.join(" · ") : null
  }, [prettyContactData.contactPhoneNumber, prettyContactData.contactEmail, item.ContactPhoneNumber, item.ContactPhoneNumbers, item.ContactEmail])

  const handleSelectContact = useCallback(async () => {
    // Select the contact in the webphone
    await selectContact({
      contactPersonID: item.ContactPersonId,
      lastInteraction: lastInteraction,
    })

    // @deprecated The embedded frontend webphone is disabled. The Chrome extension now owns the webphone sidebar.
    // if (!isMessengerPage) {
    //   setWebphoneExpanded(true)
    // }

    // Close the spotlight after selection
    headerSearchSpotlight.close()
  }, [item.ContactPersonId, lastInteraction])

  return (
    <Spotlight.Action
      onClick={handleSelectContact}
      classNames={{
        action: classes.container,
      }}
    >
      <Group wrap="nowrap" w="100%">
        <Avatar
          size="md"
          name={prettyContactData.avatarText ?? prettyContactData.contactFullName}
          color="initials"
          classNames={{
            placeholder: classes.avatarPlaceholder,
          }}
        />

        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Highlight highlight={searchQuery} size="md" fw={550} truncate highlightStyles={{ backgroundColor: "var(--mantine-color-yellow-3)" }} className={classes.name}>
              {prettyContactData.contactFullName || ""}
            </Highlight>
            <Show if={contactCompany.length > 0}>{() => <CompanyAvatar company={{ name: contactCompany, email: prettyContactData.contactEmail }} />}</Show>
          </Group>
          {sublineText && typeof sublineText === "string" && (
            <Highlight highlight={searchQuery} size="sm" c="dimmed" truncate highlightStyles={{ backgroundColor: "var(--mantine-color-yellow-3)" }} className={classes.subline}>
              {sublineText}
            </Highlight>
          )}
        </Stack>
      </Group>
    </Spotlight.Action>
  )
})
